import React, { ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  as?: 'button' | 'a' | typeof Link;
  to?: string;
  href?: string;
}

const getButtonStyles = (variant: ButtonVariant = 'primary') => {
  switch (variant) {
    case 'primary':
      return css`
        background-color: var(--blue);
        color: var(--white);
        border: 2px solid var(--blue);
        
        &:hover, &:focus {
          background-color: #0056b3;
          border-color: #0056b3;
        }
      `;
    case 'secondary':
      return css`
        background-color: var(--color-03);
        color: var(--gray-dark);
        border: 2px solid var(--color-03);
        
        &:hover, &:focus {
          background-color: #e0a800;
          border-color: #d39e00;
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        color: var(--blue);
        border: 2px solid var(--blue);
        
        &:hover, &:focus {
          background-color: rgba(0, 123, 255, 0.1);
          color: #0056b3;
          border-color: #0056b3;
        }
      `;
    case 'text':
      return css`
        background-color: transparent;
        color: var(--blue);
        border: none;
        padding-left: 0;
        padding-right: 0;
        
        &:hover, &:focus {
          text-decoration: underline;
          background-color: transparent;
          color: #0056b3;
        }
      `;
    default:
      return '';
  }
};

const getButtonSize = (size: ButtonSize = 'medium') => {
  switch (size) {
    case 'small':
      return css`
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
      `;
    case 'medium':
      return css`
        padding: 0.5rem 1rem;
        font-size: 1rem;
      `;
    case 'large':
      return css`
        padding: 0.75rem 1.5rem;
        font-size: 1.25rem;
      `;
    default:
      return '';
  }
};

const ButtonStyles = css<ButtonProps>`
  display: inline-block;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border-radius: var(--border-radius);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  
  ${props => getButtonStyles(props.variant)}
  ${props => getButtonSize(props.size)}
  
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:disabled {
    opacity: 0.65;
    pointer-events: none;
  }
  
  text-decoration: none;
  
  &:hover {
    text-decoration: none;
  }
`;

const StyledButton = styled.button<ButtonProps>`
  ${ButtonStyles}
`;

const StyledAnchor = styled.a<ButtonProps>`
  ${ButtonStyles}
`;

const StyledLink = styled(Link)<ButtonProps>`
  ${ButtonStyles}
`;

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  as = 'button',
  to,
  href,
  ...rest
}) => {
  if (as === 'a' && href) {
    return (
      <StyledAnchor
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        href={href}
        {...rest}
      />
    );
  }
  
  if (as === Link && to) {
    return (
      <StyledLink
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        to={to}
        {...rest}
      />
    );
  }
  
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      {...rest}
    />
  );
};

export default Button; 